/**
 * Reine Anzeige-Helfer für den Session-User. Zentral hier, damit SP2
 * (Nav-User-Circle) dieselbe Kürzel-/Initialen-Logik wiederverwendet wie der
 * Profil-Header — kein Drift zwischen Avatar im Header und Circle in der Nav.
 *
 * Die Initialen-Logik spiegelt das bestehende Muster in
 * `NavigationLayout.tsx:782-789` (username || Initialen aus Name/E-Mail).
 */

export type AuthUserLike =
  | {
      firstName?: string | null;
      lastName?: string | null;
      username?: string | null;
      name?: string | null;
      email?: string | null;
    }
  | null
  | undefined;

function trimOrEmpty(v?: string | null): string {
  return (v ?? "").trim();
}

/**
 * „Nachname, Vorname" → Fallback `name` → Fallback E-Mail-Local-Part → „Benutzer".
 */
export function displayName(user: AuthUserLike): string {
  const first = trimOrEmpty(user?.firstName);
  const last = trimOrEmpty(user?.lastName);
  if (last && first) return `${last}, ${first}`;
  if (last) return last;
  if (first) return first;

  const name = trimOrEmpty(user?.name);
  if (name) return name;

  const email = trimOrEmpty(user?.email);
  if (email) return email.split("@")[0];

  return "Benutzer";
}

/**
 * Initialen aus firstName+lastName, sonst aus `name`/E-Mail, max. 3 Zeichen,
 * Uppercase. „?" wenn nichts ableitbar.
 */
export function userInitials(user: AuthUserLike): string {
  const first = trimOrEmpty(user?.firstName);
  const last = trimOrEmpty(user?.lastName);
  if (first || last) {
    const fromNames = [first[0], last[0]].filter(Boolean).join("");
    if (fromNames) return fromNames.slice(0, 3).toUpperCase();
  }

  const source = trimOrEmpty(user?.name) || trimOrEmpty(user?.email);
  if (source) {
    const initials = source
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 3)
      .join("");
    if (initials) return initials.toUpperCase();
  }

  return "?";
}

/**
 * E5: `user.username` (Kürzel) — Fallback Initialen. Für Avatar-Circle.
 * `username` wird beim Login oft nicht gesetzt (Vorplan §0a) → Fallback greift.
 */
export function userKuerzel(user: AuthUserLike): string {
  const username = trimOrEmpty(user?.username);
  if (username) return username;
  return userInitials(user);
}
